import { FileReader, FileWriter } from "@/platform"
import { BrowserFileUtils } from "@/platform/utils/BrowserFileUtils"
import StoryTree from "./StoryTree"
import ScreenHash from "./ScreenHash"
import ProjectHeader from "./ProjectHeader"
import SystemData from "./SystemData"
import GameVar from "./GameVar"
import GameStr from "./GameStr"
import GameUnite from "./GameUnite"
import UserUseRember from "./UserUseRember"
import ProjectConstant from "./ProjectConstant"
import DataMap from "./DataMap"
import GameArray from "./GameArray"
import GameObj from "./GameObj"
import MapInfo from "./MapInfo"
import DataUtils from "./DataUtils"
import type EventBase from "./EventClass/EventBase"
import GameMallLimiteModel from "./MallLimiteClass/GameMallLimiteModel"
import GameMallNewModel from "./MallNewClass/GameMallNewModel"
import WelfareModel from "./Welfare/WelfareModel"
import FileItemBuf from "./FileItemBuf"
import BitmapFontData from "./BitmapFont/BitmapFontData"
import { readLegacyOuiIntoTarget } from "./ouiRuntime"
import { buildRuntimeGameBin } from "./runtimeGameBin"
import { writeProjectBinBuffer } from "./projectBinWrite"

type ReadProgress = (value: number, message: string) => void | Promise<void>

class MainProject {

	public magic: string = "ORGDAT"
	public dataVer: number = DataUtils.dataVersion
	public dataMap: DataMap = new DataMap()
	public projectHeader: ProjectHeader = new ProjectHeader()
	public screenHash: ScreenHash = new ScreenHash(0, 0, "")
	public projectConstant: ProjectConstant = new ProjectConstant()
	public systemData: SystemData = new SystemData()
	public gameVar: GameVar = new GameVar()
	public gameVarEx: GameVar = new GameVar(true)
	public gameStr: GameStr = new GameStr()
	public gameArray: GameArray = new GameArray()
	public gameObj: GameObj = new GameObj()
	public gameUnite: GameUnite = new GameUnite()
	public userRm: UserUseRember = new UserUseRember()
	public storyTree: StoryTree = new StoryTree()
	public crc32: number = 0

	// oui皮肤使用的数据
	public UiName: string = '' // UI名称
    public Bitmap: Uint8Array = new Uint8Array(0) // 对应的bitmap
    public Width: number = 0 // 分辨率
    public Height: number = 0
	public GameMallNew: GameMallNewModel | null = null // 商城3.0数据结构
    public GameMallLimite: GameMallLimiteModel = new GameMallLimiteModel() // 限时商城数据结构
    public Welfare: WelfareModel = new WelfareModel() // 福利数据结构
    private FontColor_UI: string = ''
	public FilesBuf: FileItemBuf[] = []
	/** 浏览器版运行时位图字体包，不参与 projectS.bin 序列化。 */
	public bitmapFont: BitmapFontData | null = null


	public Storys(): Array<MapInfo> {
		return this.storyTree.Maps
	}

	public Events(index: number): Array<EventBase> {
		return this.storyTree.loadEvent(index)
	}

	private async readStep<T>(
		reader: FileReader,
		stage: string,
		action: () => T | Promise<T>,
	): Promise<T> {
		try {
			return await action()
		} catch (error) {
			const message = error instanceof Error ? error.message : String(error)
			throw new Error(`解析 projects.bin 失败：${stage}：${message}`)
		}
	}

	async readBinBuffer(
		buffer: ArrayBuffer | Uint8Array,
		onProgress?: ReadProgress,
	): Promise<void> {
		const notify = async (value: number, message: string) => {
			await onProgress?.(value, message)
		}

		const reader = new FileReader(buffer)
		await notify(10, "读取项目头…")
		await this.readStep(reader, "读取项目头", () => {
			reader.readString()
			this.dataVer = reader.readInt()
		})

		this.dataMap = new DataMap()
		await this.readStep(reader, "读取数据索引", () => this.dataMap.readBin(reader))
		
		this.projectHeader = new ProjectHeader()
		await this.readStep(reader, "读取工程头", () => this.projectHeader.readBin(reader))
		await notify(12, "读取显示信息…")
		this.screenHash = new ScreenHash(0, 0, "")
		await this.readStep(reader, "读取显示信息", () => this.screenHash.readBin(reader))
		await notify(14, "读取项目常量…")
		this.projectConstant = new ProjectConstant()
		await this.readStep(reader, "读取项目常量", () => this.projectConstant.readBin(reader))
		await notify(16, "读取系统数据…")
		this.dataVer = this.projectConstant.dataVersion
		DataUtils.dataVersion = this.dataVer

		this.systemData = new SystemData()
		await this.readStep(reader, "读取系统数据", async () => {
			await this.systemData.readBin(reader, async (value, message) => {
				await notify(16 + value * 0.3, message)
			})
		})
		await notify(50, "读取作品变量…")
		this.gameVar = new GameVar()
		await this.readStep(reader, "读取作品变量", () => this.gameVar.readBin(reader))

		await notify(58, "读取扩展变量…")
		this.gameVarEx = new GameVar(true)
		await this.readStep(reader, "读取扩展变量", () => this.gameVarEx.readBin(reader))

		await notify(66, "读取字符串变量…")
		this.gameStr = new GameStr()
		await this.readStep(reader, "读取字符串变量", () => this.gameStr.readBin(reader))

		if (DataUtils.dataVersion >= 110) {
			await notify(72, "读取数组变量…")
			this.gameArray = new GameArray()
			await this.readStep(reader, "读取数组变量", () => this.gameArray.readBin(reader))

			await notify(78, "读取对象变量…")
			this.gameObj = new GameObj()
			await this.readStep(reader, "读取对象变量", () => this.gameObj.readBin(reader))
		}
		await notify(84, "读取作品单元…")
		this.gameUnite = new GameUnite()
		await this.readStep(reader, "读取作品单元", () => this.gameUnite.readBin(reader))
		await notify(90, "读取用户记忆…")
		this.userRm = new UserUseRember()
		await this.readStep(reader, "读取用户记忆", () => this.userRm.readBin(reader))
		await notify(96, "读取故事树…")
		this.storyTree = new StoryTree()
		await this.readStep(reader, "读取故事树", () => this.storyTree.readBin(reader))
		await this.readStep(reader, "读取 CRC", () => {
			this.crc32 = reader.readInt()
		})
		await notify(100, "读取完成…")
	}

	async readBin(
		file: File,
		onProgress?: ReadProgress,
	): Promise<void> {
		const buf = await BrowserFileUtils.blobToArrayBuffer(file)
		await this.readBinBuffer(buf, onProgress)
	}

	private syncDataVersion(): void {
		const version = this.projectConstant.dataVersion || this.dataVer || DataUtils.dataLatestVersion
		this.projectConstant.dataVersion = version
		this.dataVer = version
		DataUtils.dataVersion = version
	}

	/**
	 * 写入 projects.bin：DataMap 占位 → 顺序写各模块并更新 FilePos → 回填 DataMap。
	 * StoryTree 与 MapInfo 在 DataMap 中分块；文件流上 MapInfo 仍紧接 StoryTree 元数据以兼容顺序 readBin。
	 */
	writeBinBuffer(): Uint8Array {
		this.syncDataVersion()
		return writeProjectBinBuffer(this)
	}

	writeVersionedOuiBuffer(isCreateTemplate = false, dataVersion = this.projectConstant.dataVersion || this.dataVer): Uint8Array {
		this.syncDataVersion()
		const writer = new FileWriter()
		this.writeVersionedOui(writer, isCreateTemplate, dataVersion)
		return writer.toUint8Array()
	}

	/**
	 * 浏览器版运行时导出。
	 * 当前仓库尚未完整迁入 cg_v3_data 的全部 getGameBinData 分支，
	 * 这里优先复用现有可稳定写出的系统/UI/剧情数据，满足发布链需要的 Game.bin 产物。
	 */
	writeGameBinBuffer(
		maxWidth: number,
		maxHeight: number,
		guid: string,
		onlyOneBin = true,
		runtimeFontName?: string,
	): Uint8Array {
		this.syncDataVersion()
		return buildRuntimeGameBin(this, maxWidth, maxHeight, guid, onlyOneBin, runtimeFontName)
	}

	readLegacyOui(reader: FileReader, dataVersion: number, IsNew = false): void {
		readLegacyOuiIntoTarget(this, reader, dataVersion, IsNew)
	}

    writeVersionedOui(writer: FileWriter, isCreateTemplate: boolean = false, dataVersion: number): void {
        writer.writeInt(dataVersion)
        writer.writeString(this.UiName)
        writer.writeInt(this.Bitmap.length)
        writer.writeBytes(this.Bitmap)
        writer.writeInt(this.Width)
        writer.writeInt(this.Height)

        this.systemData.writeVersionedOui(writer, dataVersion)

        this.gameVar.writeVersionedOui(writer)

        this.gameVarEx.writeVersionedOui(writer)

        this.gameStr.writeVersionedOui(writer)

        // 生成新建模板文件时，设置成false
        this.GameMallNew?.writeVersionedOui(writer, isCreateTemplate ? false : true)

        this.gameUnite.writeVersionedOui(writer)

        // 生成新建模板文件时，设置成false
        this.GameMallLimite.writeVersionedOui(writer, isCreateTemplate ? false : true)

        this.Welfare.writeVersionedOui(writer)

        // 写入资源文件
        writer.writeInt(this.FilesBuf.length)
        this.FilesBuf.forEach(item => {
            item.writeData(writer)
        })
    }
}
export default MainProject
